// Browser notifications. Tier 1: in-tab / backgrounded-tab notifications via
// the Web Notifications API. No service worker, so this stops working when
// the browser is fully closed — tier 2 (Web Push + VAPID + service worker)
// is a separate workstream.
const STORAGE_KEY = 'plunder.notifications.enabled';
export function notificationsSupported() {
    return typeof window !== 'undefined' && 'Notification' in window;
}
export function notificationsEnabled() {
    if (!notificationsSupported())
        return false;
    if (Notification.permission !== 'granted')
        return false;
    return localStorage.getItem(STORAGE_KEY) !== '0';
}
export function notificationsPermission() {
    if (!notificationsSupported())
        return 'unsupported';
    return Notification.permission;
}
/** Request OS permission. Resolves to true if granted. */
export async function requestNotificationPermission() {
    if (!notificationsSupported())
        return false;
    if (Notification.permission === 'granted') {
        localStorage.setItem(STORAGE_KEY, '1');
        return true;
    }
    if (Notification.permission === 'denied')
        return false;
    const result = await Notification.requestPermission();
    if (result === 'granted') {
        localStorage.setItem(STORAGE_KEY, '1');
        return true;
    }
    return false;
}
export function setNotificationsEnabled(enabled) {
    localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
}
/** Fire a notification. Silent no-op if disabled or unsupported. Tag dedupes
 * — passing the same tag replaces an older notification rather than stacking
 * them. Use match-id as the tag so we don't get 7 "your turn" alerts for the
 * same match across polls. */
export function notify(title, body, tag) {
    if (!notificationsEnabled())
        return;
    try {
        const n = new Notification(title, {
            body,
            tag,
            icon: '/icon-192.png', // optional; falls back to favicon if missing
            silent: false,
        });
        // Auto-close after 8s so they don't pile up.
        setTimeout(() => n.close(), 8000);
        n.onclick = () => {
            window.focus();
            n.close();
        };
    }
    catch {
        // Some browsers throw if called from non-secure or backgrounded contexts.
    }
}
// ─── Lobby/match poll-driven turn detection ──────────────────────────
//
// We track which matches have already fired a "your turn" notification this
// session so we don't re-notify on every 5s poll. The Set lives in module
// scope; it resets on full page reload (which is fine — you'd want to be
// re-notified after a reload anyway if it's still your turn).
const notifiedTurnMatches = new Set();
/** Call this with the latest match list. Fires one notification per match
 * that just flipped into "your turn" state (PvP only — solo mode doesn't need
 * notifications). */
export function checkTurnNotifications(matches) {
    if (!notificationsEnabled())
        return;
    for (const m of matches) {
        if (m.match_mode !== 'pvp')
            continue;
        if (m.status !== 'in_progress')
            continue;
        if (!(m.is_my_turn || m.is_my_pick)) {
            // Not my turn anymore — clear so we'll re-notify if it comes back later.
            notifiedTurnMatches.delete(m.id);
            continue;
        }
        if (notifiedTurnMatches.has(m.id))
            continue;
        notifiedTurnMatches.add(m.id);
        const opp = m.opponent_name ?? 'Captain';
        if (m.is_my_pick) {
            notify('⚓ Your pick at the Port', `${opp} is waiting at the Port. Tap to spend.`, m.id);
        }
        else {
            notify('⚔ Your turn', `${opp} just played. Set your plank.`, m.id);
        }
    }
    // Also surface incoming friend challenges (one notification per challenge).
    for (const m of matches) {
        if (m.challenge_role === 'incoming' && !notifiedTurnMatches.has(`chal:${m.id}`)) {
            notifiedTurnMatches.add(`chal:${m.id}`);
            notify('⚔ New challenge', `${m.opponent_name ?? 'A captain'} has challenged you to a match.`, `chal:${m.id}`);
        }
    }
}
